import { Switch, TextField } from '@chronosphereio/chrono-ui';
import { TimeSeriesAxisScale, TimeSeriesChartYAxisOptions } from '@/core';
import {
  OptionsEditorControl,
  OptionsEditorGroup,
  SettingsAutocomplete,
  UnitSelector,
} from '@/plugin-editor-components';
import { DEFAULT_UNIT, DEFAULT_Y_AXIS, Y_AXIS_CONFIG } from '../time-series-chart-model';

export interface YAxisOptionsEditorProps {
  value: TimeSeriesChartYAxisOptions;
  onChange: (yAxis: TimeSeriesChartYAxisOptions) => void;
}

type ScaleOption = { id: TimeSeriesAxisScale; label: string };

const DEFAULT_SCALE: ScaleOption = { id: 'Linear', label: 'Linear' };
const LOG_10_SCALE: ScaleOption = { id: 'Log10', label: 'Log Base 10' };
const LOG_2_SCALE: ScaleOption = { id: 'Log2', label: 'Log Base 2' };

const SCALE_OPTIONS = [DEFAULT_SCALE, LOG_10_SCALE, LOG_2_SCALE];

export function YAxisOptionsEditorControls({
  value,
  onChange,
  includeShowOption,
  includeUnitSelector = false,
}: YAxisOptionsEditorProps & { includeShowOption: boolean; includeUnitSelector?: boolean }) {
  return (
    <>
      {includeShowOption ? (
        <OptionsEditorControl
          label="Show"
          control={
            <Switch
              checked={value.show ?? DEFAULT_Y_AXIS.show}
              onChange={(e) => {
                onChange({
                  ...value,
                  show: e.target.checked,
                });
              }}
            />
          }
        />
      ) : null}
      <OptionsEditorControl
        label={Y_AXIS_CONFIG.label.label}
        control={
          <TextField
            fullWidth
            value={value.label ?? ''}
            inputProps={{ 'aria-label': 'enter y axis label' }}
            onChange={(e) =>
              onChange({
                ...value,
                label: e.target.value,
              })
            }
          />
        }
      />
      <OptionsEditorControl
        label={Y_AXIS_CONFIG.min.label}
        control={
          <TextField
            fullWidth
            type="number"
            value={value.min ?? ''}
            onChange={(e) => {
              // ensure empty value resets to undef to allow chart to calculate min
              const newValue = e.target.value ? Number(e.target.value) : undefined;
              onChange({
                ...value,
                min: newValue,
              });
            }}
            placeholder="Default"
          />
        }
      />
      <OptionsEditorControl
        label={Y_AXIS_CONFIG.max.label}
        control={
          <TextField
            fullWidth
            type="number"
            value={value.max ?? ''}
            onChange={(e) => {
              // ensure empty value resets to undef to allow chart to calculate max
              const newValue = e.target.value ? Number(e.target.value) : undefined;
              onChange({
                ...value,
                max: newValue,
              });
            }}
            placeholder="Default"
          />
        }
      />
      {includeUnitSelector ? (
        <UnitSelector
          value={value.unit ?? DEFAULT_UNIT}
          onChange={(newUnit) =>
            onChange({
              ...value,
              unit: newUnit,
            })
          }
        />
      ) : null}

      <OptionsEditorControl
        label="Scale"
        control={
          <SettingsAutocomplete
            value={SCALE_OPTIONS.find((scale) => scale.id === value.scale) ?? DEFAULT_SCALE}
            options={SCALE_OPTIONS}
            onChange={(e, scale: { label: string; id: string }) => {
              const newValue: TimeSeriesChartYAxisOptions = {
                ...value,
                scale: scale.id === DEFAULT_SCALE.id ? undefined : (scale.id as TimeSeriesAxisScale),
              };

              onChange(newValue);
            }}
            disableClearable
          ></SettingsAutocomplete>
        }
      />
    </>
  );
}

export function YAxisOptionsEditor({ value, onChange }: YAxisOptionsEditorProps) {
  return (
    <OptionsEditorGroup title="Y Axis">
      <YAxisOptionsEditorControls value={value} onChange={onChange} includeShowOption={true} />
    </OptionsEditorGroup>
  );
}
